import React from 'react'
import { GridItem } from '../../components/common/grid/GridItem'
import { Title } from '../../components/common/texts/Title'
import { t } from '../../i18n/i18n'
import { UseFormReturn, useFieldArray } from 'react-hook-form'
import { OrganisationWithUserAccountsDTO, SandboxOrganisationDTO } from '../../api/schemas/schema'
import { Button, makeStyles, MenuItem, tokens } from '@fluentui/react-components'
import { Add16Regular, MoreVertical20Regular } from '@fluentui/react-icons'
import { Text } from '../../components/common/texts/Text'
import { ControlledAccountPicker } from '../../components/common/inputs/ControlledAccountPicker'
import { ContextualMenu } from '../../components/common/menu/Menu'
import { Grid } from '../../components/common/grid/Grid'

const useStyles = makeStyles({
   sandboxName: {
      display: 'flex',
      alignItems: 'end',
      minWidth: '70px',
      height: '60px',
   },
   moreBtn: {
      display: 'flex',
      alignItems: 'flex-end',
      height: '60px',
   },
   accountPicker: {
      minWidth: '250px',
      height: '60px',
   },
   container: {
      marginTop: tokens.spacingVerticalXXL,
   },
})

type SandboxFormProps = {
   organisationForm: UseFormReturn<OrganisationWithUserAccountsDTO, any, undefined>
   upgradeToOrganisation: (sandbox: SandboxOrganisationDTO) => void
}
export const SandboxForm = ({ organisationForm, upgradeToOrganisation }: SandboxFormProps) => {
   const classes = useStyles()
   const { control, watch } = organisationForm
   const { fields, insert, remove } = useFieldArray({
      control,
      name: 'sandboxOrganisations',
   })

   const createSandboxName = (existingSandboxNumbers: string[]) => {
      existingSandboxNumbers.sort((a, b) => parseInt(a) - parseInt(b))

      for (let i = 1; i <= existingSandboxNumbers.length + 1; i++) {
         if (!existingSandboxNumbers.includes(i.toString())) {
            return `${t('Sandbox')} ${i}`
         }
      }
   }

   const handleAddSandbox = () => {
      const existingSandboxNumbers = organisationForm.getValues('sandboxOrganisations').map((sandbox: SandboxOrganisationDTO) => sandbox.name.split(' ')[1]) // Get the number after "Sandbox "

      const newSandbox: SandboxOrganisationDTO = {
         name: createSandboxName(existingSandboxNumbers),
      }

      const insertIndex = parseInt(newSandbox.name.split(' ')[1]) - 1
      insert(insertIndex, newSandbox)
   }

   const handleDeleteSandbox = (index: number) => {
      remove(index)
   }

   const handleUpgradeToOrganisation = (index: number) => {
      upgradeToOrganisation(organisationForm.getValues('sandboxOrganisations')[index])
   }

   return (
      <Grid className={classes.container}>
         <GridItem size={'12/12'}>
            <Title as="h5">{t('Sandboxes')}</Title>
         </GridItem>
         <GridItem size={'12/12'}>
            <Button size="medium" icon={<Add16Regular fontSize={20} />} appearance="transparent" onClick={handleAddSandbox}>
               {t('AddSandbox')}
            </Button>
         </GridItem>
         <>
            {fields
               .sort((a, b) => parseInt(a.name.split(' ')[1]) - parseInt(b.name.split(' ')[1]))
               .map((field, index) => (
                  <>
                     <GridItem key={`${index}-name`} size="2/12" className={classes.sandboxName}>
                        <Text>{field.name}</Text>
                     </GridItem>
                     <GridItem size="4/12" key={`${index}-picker`} className={classes.accountPicker}>
                        <ControlledAccountPicker
                           required
                           label={t('User')}
                           accounts={watch('userAccounts').filter((ua) => ua.user.personalNumber && ua.firstName && ua.lastName)}
                           control={control}
                           name={`sandboxOrganisations.${index}.userAccount`}
                        />
                     </GridItem>
                     <GridItem size="2/12" key={`${index}-options`} className={classes.moreBtn}>
                        <ContextualMenu
                           menuTrigger={<Button icon={<MoreVertical20Regular />} appearance="transparent" onClick={(e: any) => e.stopPropagation()} />}
                           menuItems={
                              <>
                                 <MenuItem
                                    onClick={(e) => {
                                       e.stopPropagation()
                                       handleUpgradeToOrganisation(index)
                                    }}
                                 >
                                    {t('UpgradeToOrganisation')}
                                 </MenuItem>
                                 <MenuItem
                                    onClick={(e) => {
                                       e.stopPropagation()
                                       handleDeleteSandbox(index)
                                    }}
                                 >
                                    {t('Delete')}
                                 </MenuItem>
                              </>
                           }
                        />
                     </GridItem>
                     <GridItem size="4/12" key={`${index}-spacer`}></GridItem>
                  </>
               ))}
         </>
      </Grid>
   )
}
